/**
 * Tailwater rating curve (HYDRAULICS §6.2).
 *
 * Kettle Narrows: TW = 582.0 + 0.0021·Q^0.90 — monotone, with a finite derivative everywhere
 * except Q = 0, which is guarded. Site B uses a table because its rating is steep enough that a
 * power law misrepresents the head collapse at high flow.
 */

import { gpow } from './constants';
import { makeMonotoneCurve, type MonotoneCurve } from './curve';
import type { SiteConfig, TailwaterModel } from './types';

const tableCache = new WeakMap<SiteConfig, MonotoneCurve>();

function tableCurve(site: SiteConfig, m: Extract<TailwaterModel, { kind: 'table' }>): MonotoneCurve {
  const hit = tableCache.get(site);
  if (hit) return hit;
  const c = makeMonotoneCurve(m.q, m.tw);
  tableCache.set(site, c);
  return c;
}

/** Tailwater elevation for a total release, m ASL. */
export function tailwaterAt(site: SiteConfig, qOut: number): number {
  const m = site.tailwater;
  if (m.kind === 'power') return m.base + m.coeff * gpow(qOut, m.exp);
  return tableCurve(site, m).eval(Math.max(0, qOut));
}

/**
 * dTW/dQ, m per m³/s — the conversion between the licence's stage ramp limit and a flow rate
 * (HYDRAULICS §9). Guarded at Q → 0 where the power law's derivative diverges.
 */
export function tailwaterSlope(site: SiteConfig, qOut: number): number {
  const m = site.tailwater;
  if (m.kind === 'power') {
    const q = Math.max(qOut, 1);
    return m.coeff * m.exp * Math.pow(q, m.exp - 1);
  }
  return Math.max(1e-9, tableCurve(site, m).slope(Math.max(0, qOut)));
}

/** Flow change per hour that corresponds to a tailwater stage rate, m³/s per hour. */
export function flowRateForStageRate(site: SiteConfig, qOut: number, stageMPerH: number): number {
  return stageMPerH / Math.max(1e-9, tailwaterSlope(site, qOut));
}
